import { useCallback, useState } from 'react';
import { authApi, employesApi } from './resources';
import { useSession } from './session.jsx';

/**
 * Le patron, aussi salarié de sa société : sa propre fiche d'employé.
 *
 * <p>Le fondateur d'une petite entreprise se verse un salaire comme chacun ;
 * il lui faut sa fiche, son contrat, ses décomptes. L'invitation — le seul
 * chemin — refusait son adresse, « déjà associée à un compte ». Le
 * rattachement se fait ici d'un geste, sans invitation ni second compte : le
 * serveur reprend la fiche à son adresse ou la crée depuis son compte, puis
 * réémet la session avec elle.
 *
 * @param {object} [options] options
 * @param {(fiche: object) => Promise<void>|void} [options.onRattache] appelé
 *        avec la fiche rendue par le serveur, avant que la session ne l'adopte
 * @returns {{estPatron: boolean, rattachee: boolean, maFicheId: number|null,
 *            estLaMienne: (employe: object) => boolean, seRattache: () => Promise<object|null>,
 *            enCours: boolean, erreur: string}} l'état et le geste
 */
export function useMaFiche({ onRattache } = {}) {
  const { session, adopteCompte } = useSession();
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');

  const estPatron = session?.role === 'patron';
  const maFicheId = estPatron ? (session?.employeId ?? null) : null;
  const rattachee = maFicheId != null;

  /**
   * Vrai si la fiche est celle du patron connecté : rattachée à son compte,
   * ou, à défaut, à son adresse — une société en production l'a souvent créée
   * avant que le rattachement n'existe.
   */
  const estLaMienne = useCallback(
    (employe) => {
      if (!estPatron || !employe) return false;
      if (rattachee) return employe.id === maFicheId;
      const adresse = (employe.email || '').trim().toLowerCase();
      return !!adresse && adresse === (session?.email || '').trim().toLowerCase();
    },
    [estPatron, rattachee, maFicheId, session?.email],
  );

  const seRattache = useCallback(async () => {
    setEnCours(true);
    setErreur('');
    try {
      const { fiche, compte } = await employesApi.rattacheMoi();
      if (onRattache) await onRattache(fiche);
      adopteCompte(compte);
      return fiche;
    } catch (e) {
      setErreur(e?.message || 'Le rattachement n’a pas abouti. Réessaie.');
      return null;
    } finally {
      setEnCours(false);
    }
  }, [onRattache, adopteCompte]);

  /**
   * Après la suppression de sa propre fiche : le compte, resté ouvert, est
   * relu — l'écran disait encore « Ta fiche est rattachée » et proposait
   * « Mon espace terrain » vers une fiche effacée.
   */
  const fichePerdue = useCallback(async () => {
    try {
      adopteCompte(await authApi.moi());
    } catch {
      // Sans réponse, l'écran se remet à jour à la prochaine ouverture.
    }
  }, [adopteCompte]);

  return {
    estPatron,
    rattachee,
    maFicheId,
    estLaMienne,
    seRattache,
    fichePerdue,
    enCours,
    erreur,
  };
}
